import '@explorins/pers-shared';
import { n as normalizeToPaginated, b as buildPaginationParams } from './pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic Redemption API Client (UPDATED - RESTful Design)
 *
 * Updated to work with the new RESTful controller structure:
 * - /redemptions (main redemption management)
 * - /redemption-types (redemption type management)
 * - /redemption-tokens (token unit management)
 * - /redemption-redeems (redemption execution and history)
 *
 * Handles redemption operations using the PERS backend with intelligent access detection.
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 */
class RedemptionApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/redemptions';
        this.typesPath = '/redemptions/types';
    }
    // ==========================================
    // PUBLIC OPERATIONS (Project Key)
    // ==========================================
    /**
     * UNIFIED: Get redemptions with intelligent access control
     *
     * Intelligent endpoint that adapts based on authentication:
     * - Public users: Get active redemptions only
     * - Admin users: Get all redemptions with optional filtering
     *
     * @param options - Filter and pagination options (active, search, adminAccess, startDate, endDate, include, page, limit, sortBy, sortOrder)
     * @returns Paginated response with redemptions
     *
     * @example
     * ```typescript
     * // Get redemptions with redeem count included
     * const redemptions = await api.getRedemptions({ include: ['redeemCount'] });
     *
     * // Filter by date range
     * const dateFiltered = await api.getRedemptions({
     *   startDate: new Date('2026-05-01'),
     *   endDate: new Date('2026-06-30')
     * });
     * ```
     */
    async getRedemptions(options) {
        const params = [];
        // Always include pagination params to ensure paginated response
        params.push(`page=${options?.page || 1}`);
        params.push(`limit=${options?.limit || 50}`);
        if (options?.active !== undefined)
            params.push(`active=${options.active}`);
        if (options?.search)
            params.push(`search=${encodeURIComponent(options.search)}`);
        // Support single tag or array of tags (multiple ?tags=a&tags=b)
        if (options?.tags) {
            const tagsArray = Array.isArray(options.tags) ? options.tags : [options.tags];
            tagsArray.forEach(t => params.push(`tags=${encodeURIComponent(t)}`));
        }
        if (options?.startDate) {
            const dateStr = options.startDate instanceof Date ? options.startDate.toISOString() : options.startDate;
            params.push(`startDate=${encodeURIComponent(dateStr)}`);
        }
        if (options?.endDate) {
            const dateStr = options.endDate instanceof Date ? options.endDate.toISOString() : options.endDate;
            params.push(`endDate=${encodeURIComponent(dateStr)}`);
        }
        if (options?.sortBy)
            params.push(`sortBy=${options.sortBy}`);
        if (options?.sortOrder)
            params.push(`sortOrder=${options.sortOrder}`);
        if (options?.include?.length)
            params.push(`include=${options.include.join(',')}`);
        const response = await this.apiClient.get(`${this.basePath}?${params.join('&')}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get redemption types
     *
     * Updated: /redemption/type → /redemption-types
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with redemption types
     */
    async getRedemptionTypes(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`${this.typesPath}?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get redemption by ID
     *
     * Updated: /redemption/:id → /redemptions/:id
     *
     * @param id - Redemption UUID
     * @param include - Relations to include: 'redeemCount'
     *
     * @example
     * ```typescript
     * // Get redemption with redeem count
     * const redemption = await api.getRedemptionById('123', ['redeemCount']);
     * console.log(redemption.redeemCount);
     * ```
     */
    async getRedemptionById(id, include) {
        const params = include?.length ? `?include=${include.join(',')}` : '';
        return this.apiClient.get(`${this.basePath}/${id}${params}`);
    }
    /**
     * PUBLIC: Get available supply for redemption
     *
     * Updated: /redemption/:id/available-supply → /redemptions/:id/supply
     */
    async getRedemptionAvailableSupply(id) {
        const result = await this.apiClient.get(`${this.basePath}/${id}/supply`);
        return result.available;
    }
    // ==========================================
    // REDEMPTION EXECUTION (UNIFIED ENDPOINT)
    // ==========================================
    /**
     * Execute redemption (unified endpoint)
     *
     * NEW: POST /redemption-redeems - Role-based processing
     * - USER: Direct user redemption processing
     * - ADMIN: Can process redemptions for any account type
     * - BUSINESS: Process redemptions for customers
     *
     * @param request - Redemption request with optional context for dynamic NFT generation
     */
    async redeemRedemption(request) {
        return this.apiClient.post(this.basePath + '/redeems', request);
    }
    // ==========================================
    // REDEMPTION REDEEMS QUERIES (NEW ENDPOINTS)
    // ==========================================
    /**
     * UNIFIED: Get redemption redeems with filtering and intelligent access
     *
     * Role-based access with unified filtering:
     * - Users: See only their own redeems (userId/businessId filters ignored)
     * - Admins: Can filter by userId, businessId, redemptionId, or status
     *
     * @param filters - Filter and pagination options (redemptionId, userId, businessId, status, dateFrom, dateTo, myRedeems, page, limit, sortBy, sortOrder)
     * @param include - Optional relations to include (redemption, user, business, transactions)
     * @returns Paginated response with redemption redeems
     */
    async getRedemptionRedeems(filters, include) {
        let url = `${this.basePath}/redeems`;
        const params = buildPaginationParams(filters);
        // Use convenience endpoint for user's own redeems
        if (filters?.myRedeems) {
            url = `${this.basePath}/redeems/me`;
            if (filters?.redemptionId) {
                params.append('redemptionId', filters.redemptionId);
            }
            if (filters?.status) {
                params.append('status', filters.status);
            }
            if (filters?.dateFrom) {
                params.append('dateFrom', filters.dateFrom instanceof Date ? filters.dateFrom.toISOString() : filters.dateFrom);
            }
            if (filters?.dateTo) {
                params.append('dateTo', filters.dateTo instanceof Date ? filters.dateTo.toISOString() : filters.dateTo);
            }
            // Add include parameter
            if (include && include.length > 0) {
                include.forEach(relation => params.append('include', relation));
            }
            const response = await this.apiClient.get(`${url}?${params.toString()}`);
            return normalizeToPaginated(response);
        }
        // Use admin endpoint with filtering
        if (filters?.redemptionId)
            params.append('redemptionId', filters.redemptionId);
        if (filters?.userId)
            params.append('userId', filters.userId);
        if (filters?.businessId)
            params.append('businessId', filters.businessId);
        if (filters?.status)
            params.append('status', filters.status);
        if (filters?.dateFrom)
            params.append('dateFrom', filters.dateFrom instanceof Date ? filters.dateFrom.toISOString() : filters.dateFrom);
        if (filters?.dateTo)
            params.append('dateTo', filters.dateTo instanceof Date ? filters.dateTo.toISOString() : filters.dateTo);
        // Add include parameter
        if (include && include.length > 0) {
            include.forEach(relation => params.append('include', relation));
        }
        const response = await this.apiClient.get(`${url}?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * UNIFIED: Get specific redemption redeem by ID
     * @param id - Redemption redeem ID
     * @param include - Optional relations to include (redemption, user, business)
     */
    async getRedemptionRedeemById(id, include) {
        const params = new URLSearchParams();
        // Add include parameter
        if (include && include.length > 0) {
            include.forEach(relation => params.append('include', relation));
        }
        const url = `${this.basePath}/redeems/${id}${params.toString() ? `?${params.toString()}` : ''}`;
        return this.apiClient.get(url);
    }
    // ==========================================
    // USER OPERATIONS (JWT + Project Key)
    // ==========================================
    /**
     * UNIFIED: Get user redemption history
     *
     * Uses convenience endpoint /redemption-redeems/me with optional filtering
     * @param filters - Optional filters (redemptionId, status)
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @param include - Optional relations to include (redemption, user, business, transactions)
     * @returns Paginated response with user's redemption redeems
     */
    async getUserRedeems(filters, options, include) {
        return this.getRedemptionRedeems({ myRedeems: true, ...filters, ...options }, include);
    }
    // ==========================================
    // ADMIN OPERATIONS (Tenant Admin JWT)
    // ==========================================
    /**
     * ADMIN: Create redemption
     *
     * Updated: /redemption/admin → /redemptions
     */
    async createRedemption(redemption) {
        return this.apiClient.post(`${this.basePath}`, redemption);
    }
    /**
     * ADMIN: Update redemption
     *
     * Updated: /redemption/admin/:id → /redemptions/:id
     */
    async updateRedemption(id, redemptionCreateRequest) {
        return this.apiClient.put(`${this.basePath}/${id}`, redemptionCreateRequest);
    }
    /**
     * UNIFIED: Toggle redemption active status
     *
     * Updated: /redemption/admin/:id/toggle-active → /redemptions/:id/status
     * Following standard /status pattern used across domains
     */
    async toggleRedemptionActive(redemptionId) {
        return this.apiClient.put(`${this.basePath}/${redemptionId}/status`, {});
    }
    /**
     * ADMIN: Delete redemption
     *
     * Updated: /redemption/admin/:id → /redemptions/:id
     */
    async deleteRedemption(id) {
        return this.apiClient.delete(`${this.basePath}/${id}`);
    }
    /**
     * ADMIN: Create redemption type
     *
     * Updated: /redemption/admin/type → /redemption-types
     */
    async createRedemptionType(redemptionType) {
        return this.apiClient.post(this.typesPath, redemptionType);
    }
    /**
     * ADMIN: Update redemption type
     *
     * @param redemptionType - Updated redemption type data (must include id)
     */
    async updateRedemptionType(redemptionType) {
        return this.apiClient.put(`${this.typesPath}/${redemptionType.id}`, redemptionType);
    }
    /**
     * ADMIN: Delete redemption type
     *
     * @param id - Redemption type ID to delete
     */
    async deleteRedemptionType(id) {
        return this.apiClient.delete(`${this.typesPath}/${id}`);
    }
    // ==========================================
    // TOKEN UNIT MANAGEMENT (Admin)
    // ==========================================
    /**
     * ADMIN: Create redemption token unit
     *
     * Updated: /redemption/admin/:id/token-units → /redemption-tokens/:id/
     */
    async createRedemptionTokenUnit(redemptionId, redemptionTokenUnit) {
        return this.apiClient.post(`${this.basePath}/${redemptionId}/tokens`, redemptionTokenUnit);
    }
    /**
     * ADMIN: Update redemption token unit
     *
     * Updated: /redemption/admin/:id/token-units/:tokenUnitId → /redemption-tokens/:id/:tokenUnitId
     */
    async updateRedemptionTokenUnit(redemptionId, tokenUnitId, redemptionTokenUnit) {
        return this.apiClient.put(`${this.basePath}/${redemptionId}/tokens/${tokenUnitId}`, redemptionTokenUnit);
    }
    /**
     * ADMIN: Delete redemption token unit
     *
     * Updated: /redemption/admin/:id/token-units/:tokenUnitId → /redemption-tokens/:id/:tokenUnitId
     */
    async deleteRedemptionTokenUnit(redemptionId, redemptionTokenUnitId) {
        return this.apiClient.delete(`${this.basePath}/${redemptionId}/tokens/${redemptionTokenUnitId}`);
    }
    // ==========================================
    // CONVENIENCE METHODS (Legacy Support)
    // ==========================================
    /**
     * Convenience: Get active redemptions (public access)
     *
     * @deprecated Use getRedemptions({ active: true }) instead for paginated results
     */
    async getActiveRedemptions() {
        const result = await this.getRedemptions({ active: true });
        return [...result.data];
    }
    // ==========================================
    // CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export redemptions to CSV
     *
     * Returns a CSV file with redemption data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     */
    async exportCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
    /**
     * ADMIN: Export redemption redeems to CSV
     *
     * Returns a CSV file with all redemption redeem data.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     */
    async exportRedeemsCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/redeems/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
}

/**
 * Platform-Agnostic Redemption Service
 *
 * Contains redemption business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 *
 * Focuses only on actual backend capabilities.
 */
class RedemptionService {
    constructor(redemptionApi) {
        this.redemptionApi = redemptionApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * UNIFIED: Get redemptions with intelligent access control and pagination
     * @param options.active - Filter by active status
     * @param options.search - Free text search across name, description, tags (case-insensitive)
     * @param options.tags - Filter by tag(s) - single tag or array for OR match
     * @param options.adminAccess - Force admin access
     * @param options.startDate - Filter redemptions starting on or after this date
     * @param options.endDate - Filter redemptions ending on or before this date
     * @param options.include - Include relations: 'redeemCount'
     * @param options.page - Page number (1-indexed)
     * @param options.limit - Results per page
     * @param options.sortBy - Field to sort by ('name' | 'createdAt' | 'startDate')
     * @param options.sortOrder - Sort direction ('ASC' | 'DESC')
     */
    async getRedemptions(options) {
        return this.redemptionApi.getRedemptions(options);
    }
    /**
     * Get a specific redemption by ID
     * @param id - Redemption UUID
     * @param include - Optional relations to include: 'redeemCount'
     */
    async getRedemptionById(id, include) {
        return this.redemptionApi.getRedemptionById(id, include);
    }
    /**
     * Convenience: Get active redemptions (public)
     */
    async getActiveRedemptions() {
        return this.redemptionApi.getActiveRedemptions();
    }
    /**
     * Get all redemption types with pagination
     */
    async getRedemptionTypes(options) {
        return this.redemptionApi.getRedemptionTypes(options);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Redeem a redemption with optional dynamic context
     *
     * @param request - Redemption request (can be just redemptionId string or full request with context)
     */
    async redeemRedemption(request) {
        // Support both simple string and full request object
        const requestBody = typeof request === 'string'
            ? { redemptionId: request }
            : request;
        return this.redemptionApi.redeemRedemption(requestBody);
    }
    /**
     * UNIFIED: Get redemption redeems with filtering and pagination
     * @param filters - Filter options (redemptionId, userId, businessId, status, dateFrom, dateTo, myRedeems)
     * @param include - Optional relations to include (redemption, user, business, transactions)
     */
    async getRedemptionRedeems(filters, include) {
        return this.redemptionApi.getRedemptionRedeems(filters, include);
    }
    /**
     * Convenience: Get user redemptions with pagination and filtering
     * @param filters - Optional filters (redemptionId, status)
     * @param options - Pagination options
     * @param include - Optional relations to include (redemption, user, business, transactions)
     */
    async getUserRedeems(filters, options, include) {
        return this.redemptionApi.getUserRedeems(filters, options, include);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * Convenience: Get redemptions as admin with filtering and search
     * @param options - Filter options (active, search, tags, pagination)
     */
    async getRedemptionsAsAdmin(options) {
        return this.redemptionApi.getRedemptions({ ...options, adminAccess: true });
    }
    /**
     * ADMIN: Create redemption
     */
    async createRedemption(redemption) {
        return this.redemptionApi.createRedemption(redemption);
    }
    /**
     * ADMIN: Update redemption
     */
    async updateRedemption(id, redemptionCreateRequest) {
        return this.redemptionApi.updateRedemption(id, redemptionCreateRequest); // ✅ CORRECTED: Fixed parameter
    }
    /**
     * ADMIN: Toggle redemption active status
     */
    async toggleRedemptionActive(redemptionId) {
        return this.redemptionApi.toggleRedemptionActive(redemptionId);
    }
    /**
     * ADMIN: Create redemption token unit
     */
    async createRedemptionTokenUnit(redemptionId, redemptionTokenUnit) {
        return this.redemptionApi.createRedemptionTokenUnit(redemptionId, redemptionTokenUnit);
    }
    /**
     * ADMIN: Delete redemption token unit
     */
    async deleteRedemptionTokenUnit(redemptionId, redemptionTokenUnitId) {
        return this.redemptionApi.deleteRedemptionTokenUnit(redemptionId, redemptionTokenUnitId);
    }
    // ==========================================
    // REDEMPTION TYPE MANAGEMENT (Admin)
    // ==========================================
    /**
     * ADMIN: Create redemption type
     */
    async createRedemptionType(data) {
        return this.redemptionApi.createRedemptionType(data);
    }
    /**
     * ADMIN: Update redemption type
     */
    async updateRedemptionType(data) {
        return this.redemptionApi.updateRedemptionType(data);
    }
    /**
     * ADMIN: Delete redemption
     */
    async deleteRedemption(id) {
        return this.redemptionApi.deleteRedemption(id);
    }
    /**
     * ADMIN: Delete redemption type
     */
    async deleteRedemptionType(id) {
        return this.redemptionApi.deleteRedemptionType(id);
    }
}

export { RedemptionApi as R, RedemptionService as a };
//# sourceMappingURL=redemption-service-CQ73aUTe.js.map
